import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  RotateCcw, 
  ZoomIn, 
  ZoomOut, 
  Move3d, 
  Eye, 
  Grid3X3, 
  Camera, 
  Download, 
  Box, 
  Axis3d, 
  SunMoon, 
  Ruler,
  Maximize2,
  Minimize2,
  Settings,
  Sliders,
  Layers,
  Palette,
  Check
} from 'lucide-react';
import { cn } from '../../lib/utils';

export interface ViewerControls {
  reset: () => void;
  takeScreenshot: () => Promise<string>;
  exportGLTF: () => Promise<Blob>;
  toggleWireframe: () => void;
  toggleAxes: () => void;
  toggleMeasurements: () => void;
  setEnvironment: (preset: string) => void;
  setQuality: (quality: 'low' | 'medium' | 'high') => void;
  setFinish: (finish: 'glossy' | 'matte') => void;
}

interface ViewerControlsProps {
  controls: ViewerControls;
  state: {
    showWireframe: boolean;
    showAxes: boolean;
    showMeasurements: boolean;
    activeEnvironment: string;
    quality: 'low' | 'medium' | 'high';
    finish: 'glossy' | 'matte';
    activeViewMode?: string;
  };
  onViewChange: (view: string) => void;
  onFinishChange?: (finish: 'glossy' | 'matte') => void;
  compact?: boolean;
  className?: string;
}

const buttonVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.1 },
  tap: { scale: 0.95 }
};

const environmentPresets = [
  'apartment', 
  'city', 
  'dawn', 
  'forest', 
  'lobby', 
  'night', 
  'park', 
  'studio', 
  'sunset', 
  'warehouse'
];

export const ViewerControls: React.FC<ViewerControlsProps> = ({
  controls,
  state,
  onViewChange,
  onFinishChange,
  compact = false,
  className = ''
}) => {
  const [showSettings, setShowSettings] = useState(false);
  
  const handleScreenshot = async () => {
    try {
      const dataUrl = await controls.takeScreenshot();
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'surface-preview.png';
      link.click();
    } catch (error) {
      console.error('Failed to take screenshot:', error);
    }
  };

  const handleExport = async () => {
    try {
      const blob = await controls.exportGLTF();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'surface-model.gltf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export model:', error);
    }
  };

  const handleFinishChange = (finish: 'glossy' | 'matte') => {
    controls.setFinish(finish);
    if (onFinishChange) {
      onFinishChange(finish);
    }
  };

  return (
    <div className={cn(
      "absolute top-0 left-0 right-0 z-10",
      className
    )}>
      <div className="flex items-center justify-between p-2 sm:p-4">
        {/* View Controls */}
        {!compact && (
          <div className="flex gap-1 sm:gap-2">
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => onViewChange('3d')}
              className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
              title="3D View"
            >
              <Move3d className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => onViewChange('top')}
              className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
              title="Top View"
            >
              <Grid3X3 className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => onViewChange('front')}
              className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
              title="Front View"
            >
              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
          </div>
        )}

        {/* Material Finish Selector - Only in compact mode */}
        {compact && (
          <div className="flex gap-1">
            {['glossy', 'matte'].map((finishOption) => (
              <motion.button
                key={finishOption}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => handleFinishChange(finishOption as 'glossy' | 'matte')}
                className={`p-1 sm:p-2 rounded-lg flex items-center ${
                  state.finish === finishOption
                    ? 'bg-white text-gray-900'
                    : 'bg-gray-800/80 text-gray-400 hover:text-white'
                }`}
                title={`${finishOption.charAt(0).toUpperCase() + finishOption.slice(1)} Finish`}
              >
                <SunMoon className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            ))}
          </div>
        )}

        {/* Camera Controls */}
        <div className="flex gap-1 sm:gap-2">
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={controls.reset}
            className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
            title="Reset View"
          >
            <RotateCcw className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
          
          {!compact && (
            <>
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => controls.toggleWireframe()}
                className={`p-1 sm:p-2 rounded-lg ${
                  state.showWireframe 
                    ? 'bg-gold text-white' 
                    : 'bg-gray-800/80 text-gray-400 hover:text-white'
                }`}
                title="Toggle Wireframe"
              >
                <Box className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
              
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => controls.toggleAxes()}
                className={`p-1 sm:p-2 rounded-lg ${
                  state.showAxes
                    ? 'bg-gold text-white'
                    : 'bg-gray-800/80 text-gray-400 hover:text-white'
                }`}
                title="Toggle Axes"
              >
                <Axis3d className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
              
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={() => controls.toggleMeasurements()}
                className={`p-1 sm:p-2 rounded-lg ${
                  state.showMeasurements
                    ? 'bg-gold text-white'
                    : 'bg-gray-800/80 text-gray-400 hover:text-white'
                }`}
                title="Toggle Measurements"
              >
                <Ruler className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            </>
          )}
          
          {!compact && (
            <>
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handleScreenshot}
                className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
                title="Take Screenshot"
              >
                <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
              
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handleExport}
                className="p-1 sm:p-2 rounded-lg bg-gray-800/80 text-gray-400 hover:text-white"
                title="Export 3D Model"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            </>
          )}
          
          {!compact && (
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => setShowSettings(!showSettings)}
              className={`p-1 sm:p-2 rounded-lg ${
                showSettings
                  ? 'bg-gold text-white'
                  : 'bg-gray-800/80 text-gray-400 hover:text-white'
              }`}
              title="Settings"
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
          )}
        </div>
      </div>
      
      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && !compact && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-800/90 backdrop-blur-sm border-t border-gray-700"
          >
            <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Environment Presets */}
              <div>
                <h3 className="text-white text-sm font-medium mb-2">Environment</h3>
                <div className="grid grid-cols-2 gap-2">
                  {environmentPresets.slice(0, 6).map(preset => (
                    <motion.button
                      key={preset}
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => controls.setEnvironment(preset)}
                      className={`px-3 py-2 rounded-lg text-xs capitalize ${
                        state.activeEnvironment === preset
                          ? 'bg-gold text-white'
                          : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {preset}
                    </motion.button>
                  ))}
                </div>
              </div>
              
              {/* Quality Settings */}
              <div>
                <h3 className="text-white text-sm font-medium mb-2">Quality</h3>
                <div className="grid grid-cols-3 gap-2">
                  {['low', 'medium', 'high'].map(qualityOption => (
                    <motion.button
                      key={qualityOption}
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => controls.setQuality(qualityOption as 'low' | 'medium' | 'high')}
                      className={`px-3 py-2 rounded-lg text-xs capitalize flex items-center justify-center gap-1 ${
                        state.quality === qualityOption
                          ? 'bg-gold text-white'
                          : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {state.quality === qualityOption && (
                        <Check className="w-3 h-3" />
                      )}
                      {qualityOption}
                    </motion.button>
                  ))}
                </div>
              </div>
              
              {/* Material Finish */}
              <div>
                <h3 className="text-white text-sm font-medium mb-2">Material Finish</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['glossy', 'matte'].map(finishOption => (
                    <motion.button
                      key={finishOption}
                      variants={buttonVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onClick={() => handleFinishChange(finishOption as 'glossy' | 'matte')}
                      className={`px-3 py-2 rounded-lg text-xs capitalize flex items-center justify-center gap-1 ${
                        state.finish === finishOption
                          ? 'bg-gold text-white'
                          : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      <SunMoon className="w-3 h-3 mr-1" />
                      {finishOption}
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};